import React from "react";

const OrderSummary = ({ customerInfo, setCustomerInfo, grandTotal, subtotal, salesTax, finalTotal }) => {
  const taxRate = 6.625;

  const handleDiscountChange = (e) => {
    const value = e.target.value;
    
    // If input is empty or invalid, set to 0
    if (value === "" || isNaN(value)) {
      setCustomerInfo({ ...customerInfo, discount: 0 });
    } else {
      // Convert to number and ensure it's between 0-100
      const discountValue = Math.min(Math.max(parseFloat(value), 0), 100);
      setCustomerInfo({ ...customerInfo, discount: discountValue });
    }
  };

  const handleDiscountBlur = (e) => {
    const value = e.target.value;
    
    // If input is empty on blur, set to 0
    if (value === "") {
      setCustomerInfo({ ...customerInfo, discount: 0 });
    }
  };

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 mt-8 border border-indigo-100 slide-in">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <div className="w-2 h-6 bg-green-500 rounded-full mr-3"></div>
        Order Summary & Tax Calculation
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Discount Input */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-indigo-100">
          <label className="block text-lg font-semibold text-gray-700 mb-4">
            Overall Discount (%)
          </label>
          <input
            type="number"
            value={customerInfo.discount === 0 ? "" : customerInfo.discount}
            onChange={handleDiscountChange}
            onBlur={handleDiscountBlur}
            className="w-full px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-700 font-semibold text-center text-lg"
            placeholder="0"
            min="0"
            max="100"
          />
          <div className="mt-3 text-sm text-gray-500 text-center">
            {customerInfo.discount > 0 ? (
              <span className="text-green-600 font-semibold">
                {customerInfo.discount}% discount applied
              </span>
            ) : (
              "Enter discount percentage"
            )}
          </div>
        </div>

        {/* Cost Breakdown */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-indigo-100 lg:col-span-2">
          <div className="space-y-4">
            {/* Subtotal */}
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">Items Subtotal:</span>
              <span className="font-semibold text-gray-800">
                ${subtotal.toFixed(2)}
              </span>
            </div>

            {/* Discount */}
            {customerInfo.discount > 0 && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-green-600">
                  Discount ({customerInfo.discount}%):
                </span>
                <span className="font-semibold text-green-600">
                  - ${(subtotal * (customerInfo.discount / 100)).toFixed(2)}
                </span>
              </div>
            )}

            {/* Grand Total (Before Tax) */}
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-700 font-medium">Total (Before Tax):</span>
              <span className="font-semibold text-gray-800">
                ${grandTotal.toFixed(2)}
              </span>
            </div>

            {/* Sales Tax */}
            <div className="flex justify-between items-center py-2 border-b border-blue-100 bg-blue-50 px-3 rounded">
              <span className="text-blue-700 font-medium">
                Sales Tax ({taxRate}%):
              </span>
              <span className="font-bold text-blue-700">
                + ${salesTax.toFixed(2)}
              </span>
            </div>

            {/* Final Total */}
            <div className="flex justify-between items-center py-3 bg-gradient-to-r from-indigo-500 to-purple-600 -mx-3 -mb-3 px-6 rounded-b-xl">
              <span className="text-white font-bold text-lg">Final Total:</span>
              <span className="text-white font-bold text-2xl">
                ${finalTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tax Information */}
      <div className="mt-6 bg-white rounded-xl p-4 border border-indigo-100">
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">
            💡 <strong>Tax Breakdown:</strong> A sales tax of {taxRate}% is applied to all orders.
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="text-center">
              <div className="font-semibold text-gray-700">Subtotal</div>
              <div className="text-gray-800">${subtotal.toFixed(2)}</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-blue-600">Sales Tax Paid</div>
              <div className="text-blue-600 font-bold">${salesTax.toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;